import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { colors } from "../../../../utils";
import { ASSETS } from "../../../../utils/config";
import TextView from "../../../TextView";
import {
  getBusinessBoxPath,
  getUserProfilePath,
  getWheelUploadPath,
  handleFileUpload,
} from "../../../../utils/fileUpload";
import BrandWheelLoader from "../../../BrandWheelLoader";
import Button from "../../../Button";
import { UploadCancelledError } from "../../../../utils/s3Helper";
import { getLoggedInUser, getRandomId } from "../../../../utils/helpers";
import {
  getSelectedSegment,
  getWheelId,
} from "../../../../redux/selectors/segmentBuilderSelectors";
import { useSelector } from "react-redux";
import { saveRecentMedia, checkSegmentVideoWeb } from "../../../../services/api";
import FeatureEligibility from "../../../FeatureEligibility";
import { FEATURES } from "../../../../constants/features";
import { useIfUserEligible } from "../../../../hooks/useIfUserEligible";
import tokens from "../../../../theme/tokens";
import { toErrorToastMessage } from "../../../../utils/errorToastMessage";
import {
  MAX_FILE_SIZE_MB,
  maxFileSizeLabelForType,
  maxFileSizeMbForType,
  partitionUploadFiles,
  VIDEO_MAX_FILE_SIZE_LABEL,
} from "./validation";
import { IMAGE_ACCEPT } from "../../BlockEditorPane/editors/imageUpload";
import {
  beginPendingUpload,
  endActiveUpload,
  setActiveUploadPercent,
  startActiveUpload,
  useActiveUpload,
} from "./activeUploads";
// kelly-1005-P1-portrait-video-length-before-upload
import {
  readLocalVideoDurationSeconds,
  videoDurationCapLabel,
  videoTooLargeMessage,
} from "../../../../utils/videoDuration";
// ISP-9999 (gordon-0904-over-limit-upload-must-block)
import useStorageLimit from "../../../../hooks/useStorageLimit";
import {
  AUDIO_UPLOAD_EXTENSIONS,
  audioAcceptAttribute,
} from "../../../../utils/audioFormats";

// T22.676: the Segment Video block's oversize message (generic cap only; a
// surface with its own videoMaxBytes, e.g. Portrait, keeps videoTooLargeMessage).
// #7789 (T22.676) used MAX_FILE_SIZE_MB here and #7793 (T22.677) dropped its import,
// leaving a module-load ReferenceError; the generic video cap is now 256MB.
export const GENERIC_VIDEO_TOO_LARGE = `Upload exceeds ${VIDEO_MAX_FILE_SIZE_LABEL} limit. Trim or compress the video, or paste a YouTube or Vimeo link under Embed URL.`;

// gordon-1005-P2-portrait-video-trimmer: lazy-loaded so its bytes (plus the
// shared SettingsSlider it pulls in) are fetched only once a video actually
// needs trimming — every other UploadMedia mount (the overwhelming majority:
// images, documents, audio, in-cap video) never requests this chunk.
const VideoTrimmer = lazy(() => import("./VideoTrimmer"));

// T22.354 (Kelly 2026-10-16, ".mov under 50mb ... error messages"): the
// HEVC -> H.264 web copy used to be made INSIDE the segment-video-web request.
// MEASURED on web-stg: a 23.5 MB / 30s iPhone .mov took 38s there and died in
// the browser at ~60s (gateway) under load, so the block was handed the raw
// HEVC file -> "Video unavailable". The backend now answers at once with
// `pending: true` and converts on a worker; we poll until it is done.
export const SEGMENT_VIDEO_WEB_POLL_MS = 3000;
export const SEGMENT_VIDEO_WEB_MAX_WAIT_MS = 6 * 60 * 1000;
// review-1008-Frontend-7373: once the backend has said `pending`, a poll that
// errors (gateway 502 during a deploy, a network blip) is retried -- the call
// is idempotent and the worker is still converting. MEASURED on web-stg
// main.aebe2d7e: one 502 on poll 3 handed the block the raw HEVC file ->
// "Video unavailable". A 4xx answer, or this many errors in a row, still
// falls back to the raw upload as before.
export const SEGMENT_VIDEO_WEB_MAX_POLL_ERRORS = 5;
const isRetryablePollError = (err) => {
  const status = err?.response?.status;
  return !status || status >= 500 || status === 429;
};
const waitOrAbort = (ms, signal) =>
  new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true }
    );
  });

const allowedMimeTypes = {
  image: ["png", "jpg", "jpeg", "gif", "heic", "svg", "webp", "avif"],
  images: ["png", "jpg", "jpeg", "gif", "heic", "svg", "webp", "avif"],
  video: ["mp4", "mov", "avi", "mkv"],
  documents: ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "csv"],
  document: ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "csv"],
  // QA-97 — the Audio Block accepts every audio container the backend does,
  // read from the ONE list (src/utils/audioFormats.js) rather than a second
  // hand-kept copy. Audiobook containers (.m4b) and Audible files (.aa/.aax)
  // upload and store; whether they can PLAY is a separate question the same
  // module answers, and the surfaces say so instead of showing a dead player.
  audio: AUDIO_UPLOAD_EXTENSIONS,
  audios: AUDIO_UPLOAD_EXTENSIONS,
};

const Container = styled.div`
  width: 100%;
  background-color: ${colors.background};
  border-radius: 8px;
  border: 1px dashed ${colors.purple};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  height: 140px;
  margin: 10px 0px;
  cursor: pointer;
  transition: border-color 120ms ease, background-color 120ms ease;

  /* CAP-A11Y-02 (WCAG 2.4.7): this dropzone is now keyboard-focusable, so it
     needs the app's STANDARD focus ring — the same token the picker tabs use
     (see MediaSelection PICKER_TAB_FOCUS_RING_SX / merged #2252). */
  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.shadow.focus};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const BoldText = styled.span`
  font-weight: bold;
  text-decoration: underline;
  cursor: pointer;
`;

const Icon = styled.img`
  margin-bottom: 10px;
  width: 50px;
  height: 50px;
`;

const HiddenInput = styled.input`
  display: none;
`;

const InfoContainer = styled.div`
  margin-top: 5px;
  padding: 5px 10px;
  border-radius: 4px;
  margin-bottom: 5px;
`;

export default function UploadMedia({
  type = "image",
  handleMedia,
  isBusinessBox = false,
  // ISP-9999 (gordon-0913-gallery-theme-wallpaper-pattern-image-parity):
  // mirrors EmbedURL's existing `pathType` prop (see
  // SegmentBuilder/Shared/EmbedURL/index.jsx getUploadPath) so a surface with
  // no wheel/business context — e.g. the Gallery Theme wallpaper picker — can
  // upload into the user's own profile-asset namespace instead of a
  // wheel-scoped one. Default "" preserves every existing call site's
  // behavior exactly (isBusinessBox ? business path : wheel path).
  pathType = "",
  media,
  setMedia,
  // ISP-9999: Wheel/Segment Wallpaper is a STATIC surface — it must not accept
  // animated GIFs. Default `true` keeps every existing caller (Image / GIF fill
  // tabs, content blocks, Portrait) byte-identical; only the wallpaper Upload
  // passes `allowGif={false}` to drop gif from the accepted formats + advertised
  // "Valid formats" text. Non-image types are unaffected.
  allowGif = true,
  // kelly-1005-P1-portrait-video-length-before-upload: opt-in caps for a video
  // surface that advertises a shorter clip length than the generic 256MB/any-
  // length video default (T22.677) (today: the Portrait panel's Video source, 15s/50MB — see
  // components/PortraitMedia/PortraitMediaPanel.jsx). Undefined on every other
  // caller (Video content block, images, documents, audio) keeps this byte-
  // identical: no headline, no duration read, no extra size check.
  videoMaxDurationSeconds,
  videoMaxBytes,
  videoDurationContextLabel = "Videos",
  // T22.143: identifies the block a video upload belongs to (the Video block
  // passes "<segment id>:<node id>"). With it, a remounted pane for the same
  // block picks a still-running upload back up (see ./activeUploads). Without
  // it (every other caller) the upload state stays local, byte-identical.
  uploadKey,
}) {
  const isImageType = type === "image" || type === "images";
  // Effective accepted extensions for THIS surface: strip "gif" only when a caller
  // (the wallpaper picker) opts out and the surface is an image surface.
  const effectiveFormats =
    !allowGif && isImageType
      ? allowedMimeTypes[type].filter((ext) => ext !== "gif")
      : allowedMimeTypes[type];
  const fileIcons = {
    image: ASSETS.image,
    images: ASSETS.image,
    video: ASSETS.video,
    document: ASSETS.document,
    documents: ASSETS.document,
    audio: ASSETS.mp3Icon,
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const fileInputRef = useRef(null);
  // stgfix-1008-B50 (after-the-50mb-failure-the-red-error-line-stays-th):
  // react-toastify's own autoClose timer (App.js ToastContainer,
  // autoClose={2000}) can get stuck paused — MEASURED live on web-stg.iyuga.net:
  // picking a second file (even a valid one) shortly after a rejection toast
  // fires leaves that toast on screen 16s+ later, overlapping the NEXT upload.
  // uploadFiles therefore force-dismisses this dropzone's own prior rejection
  // toasts the moment a new attempt starts.
  // review-1008-Frontend-6670: track the ids react-toastify RETURNS rather than
  // passing one shared fixed toastId. react-toastify drops any toast whose
  // toastId is still in its map, and a dismissed toast stays there until its
  // exit animation ends — so a shared id swallowed (a) every rejection reason
  // after the first in one drop and (b) a repeat rejection right after the
  // previous one (old toast slid out, no new toast appeared).
  const rejectionToastIdsRef = useRef([]);
  const trackRejectionToast = (id) => {
    if (id != null) rejectionToastIdsRef.current.push(id);
  };
  const dismissRejectionToasts = () => {
    rejectionToastIdsRef.current.forEach((id) => toast.dismiss(id));
    rejectionToastIdsRef.current = [];
  };
  // ISP-9999 (gordon-0923-video-upload-11s-takes-very-long, gordon-1003 real-
  // surface follow-up): this is the dropzone users ACTUALLY reach from the
  // Segment Builder's Video content block (VideoSettings.jsx mounts
  // <UploadMedia type="video">) — unlike CodeBlocks/Video/Upload.jsx, which
  // got real progress%/Cancel wiring in FE#6196 but is dead code (no importer
  // outside its own test, confirmed by grep). Live-measured on web-dev: this
  // dropzone showed only a bare "Uploading…" spinner for the whole PUT, with
  // zero percent and no way to cancel. Scoped to type==="video" (the only
  // caller that hits this branch besides type==="image", which stays
  // byte-identical) to match this bug's scope and FE#6196's own precedent.
  const [percent, setPercent] = useState(null);
  // T22.354: true while the backend converts an HEVC upload for web playback.
  const [converting, setConverting] = useState(false);
  const abortControllerRef = useRef(null);
  // gordon-1005-P2-portrait-video-trimmer: a video over videoMaxDurationSeconds
  // no longer gets refused outright — it waits here (file + its own measured
  // duration) while the lazy-loaded VideoTrimmer offers a 15s window instead.
  // null the rest of the time, including for every caller that never opts
  // into videoMaxDurationSeconds (byte-identical: this state is set in
  // exactly the one place the old rejection used to fire).
  const [trimCandidate, setTrimCandidate] = useState(null);

  const {
    isEligible,
    subscription,
    isCheckingEligibility,
    requiredSubscription,
  } = useIfUserEligible(type === "video" ? FEATURES.UPLOAD_VIDEOS : null);

  // Uniform client-side guard for EVERY upload entry point (file picker +
  // dropzone drag-drop) and BOTH branches (single + multi). Previously the file
  // PICKER (handleMediaChange) skipped the type check — only the bypassable
  // `accept` attr guarded it (C4.2: a renamed .exe reached S3) — and the
  // single/video branch skipped the 50MB size check (C4.1: a multi-GB video
  // reached S3). Validate once, here, via the pure `partitionUploadFiles`.
  const getValidFiles = (files) => {
    const { valid, rejectedType, rejectedSize } = partitionUploadFiles(
      files,
      effectiveFormats,
      // T22.677: a video is capped only at the server's presign ceiling, not 50MB.
      maxFileSizeMbForType(type)
    );
    if (rejectedType) {
      trackRejectionToast(toast.error("Some file types not supported!"));
    }
    if (rejectedSize) {
      // T22.676: a segment video over the cap used to show only "Upload exceeds
      // 50MB limit." — a Facebook-group user read that as "video won't load to
      // segments". Keep the same words, and add the way forward. Only where the
      // generic cap is the video cap (no videoMaxBytes): Portrait has no Embed
      // URL tab and keeps its own wording.
      setError(type === "video" && !videoMaxBytes ? GENERIC_VIDEO_TOO_LARGE : true);
      trackRejectionToast(
        toast.error(`Files over ${maxFileSizeLabelForType(type)} cannot be uploaded.`)
      );
    }
    // kelly-1005-P1-portrait-video-length-before-upload: a surface with its own
    // video cap (e.g. Portrait's 50MB, stricter than the generic video cap) layers a
    // second size check on top — both apply, the stricter one wins.
    if (type === "video" && videoMaxBytes) {
      const withinCap = valid.filter((file) => file.size <= videoMaxBytes);
      if (withinCap.length !== valid.length) {
        const msg = videoTooLargeMessage(videoMaxBytes, videoDurationContextLabel);
        setError(msg);
        trackRejectionToast(toast.error(`${msg}`));
      }
      return withinCap;
    }
    return valid;
  };

  // ISP-9999 (gordon-0904-over-limit-upload-must-block): this dropzone is the
  // Edit Wheel / Segment media picker. Measured on Development 2026-09-04
  // (account 12675, qa-notes/vy/dev17-deployed-verify-0904): at 275.21 MB used
  // of a 250 MB limit it still presigned (200), PUT to S3 (200) and PERSISTED
  // the image to the wheel — while Settings > Storage said uploads were paused.
  //
  // `blockUpload()` shows the shared toast and returns true when the account is
  // full. Guarding all THREE entry points (click, keyboard, drop) is what makes
  // this real: guarding only the click leaves drag-and-drop wide open.
  const {
    full: storageFull,
    disabledLabel: storageFullLabel,
    blockUpload,
  } = useStorageLimit();

  const handleClick = () => {
    if (blockUpload()) return; // never opens the chooser => no presign request
    fileInputRef.current.click();
  };

  const handleMediaChange = (e) => {
    const files = Array.from(e.target.files);
    // Belt and braces: a chooser already open when the quota read caught up,
    // or a caller that clicks the input directly, still gets refused here.
    if (files.length > 0 && blockUpload()) {
      e.target.value = null;
      return;
    }
    if (files && files.length > 0) {
      uploadFiles(files);
      e.target.value = null;
    }
  };

  const wheelId = useSelector(getWheelId);

  // legal-0919b: UploadMedia (via MediaSelection) was previously only mounted
  // behind auth-gated Segment Builder surfaces, so getLoggedInUser() always
  // returned a real user. It is now also reachable from the shared kebab
  // <ReportModal>'s Copyright reason (CopyrightReportSection -> DMCATakedown
  // "Proof of Ownership" uploader) where a transient logged-out/stale-session
  // state must not throw client-side before any request. Same fix as PR
  // #5849 (fe-0919-report-copyright-crash), applied independently here since
  // that branch isn't merged yet and this new mount point hits the same line.
  const user_id = getLoggedInUser()?.id;
  // ISP-9999: getUserProfilePath is called ONLY on the pathType==="profile"
  // branch (not hoisted into an unconditional variable like the other two
  // paths) so every existing caller/test that mocks utils/fileUpload without
  // it (pre-dating this unit) keeps working untouched.
  const uploadPath =
    pathType === "profile"
      ? getUserProfilePath({ user_id })
      : isBusinessBox
      ? getBusinessBoxPath({ user_id })
      : getWheelUploadPath({ user_id: user_id, wheel_id: wheelId });

  const getFileType = () => {
    switch (type) {
      case "images":
        return "0";
      case "image":
        return "0";
      case "video":
        return "1";
      case "documents":
        return "2";
      case "audio":
        return "3";
    }
  };
  // gordon-1005-P2-portrait-video-trimmer: the single-file branch (image /
  // video, non-multi) pulled out of uploadFiles so the trimmer's "Use this
  // part" can hand a trimmed File straight to the SAME upload path a normal
  // in-cap pick uses, without re-running getValidFiles (a derived trim output
  // isn't a user-picked file to re-validate) or the duration guard (it is
  // <= videoMaxDurationSeconds by construction).
  const uploadSingleFile = async (file) => {
    const id = getRandomId();
    const isVideo = type === "video";
    const controller = isVideo ? new AbortController() : null;
    const cancel = controller && (() => controller.abort());
    const tracked = isVideo && uploadKey != null;
    if (isVideo) {
      abortControllerRef.current = controller;
      setPercent(0);
    }
    if (tracked) startActiveUpload(uploadKey, cancel);
    // T22.294: keeps the builder's save chip off "All changes saved" until the
    // uploaded file has been handed to the page (see ./activeUploads).
    const endPending = beginPendingUpload();
    try {
      setLoading(true);

      handleMedia({ id, loading: true });
      const { location } = await handleFileUpload(
        file,
        uploadPath,
        wheelId,
        null,
        isVideo
          ? {
              signal: controller.signal,
              onProgress: ({ percent: p }) => {
                setPercent(p);
                if (tracked) setActiveUploadPercent(uploadKey, cancel, p);
              },
            }
          : undefined
      );

      // stgfix-1007-P1-iphone-video-upload-rejected: an HEVC-encoded upload
      // (iPhone's default camera codec) persists fine but can't be decoded
      // by any Chromium/Firefox browser -- MEASURED live, the block then
      // shows "Video unavailable". Ask the backend to swap it for a
      // web-safe H.264 copy when needed before handing off the final src.
      // Best-effort: any failure here (network, flag off, non-HEVC) just
      // falls back to the raw upload location -- never blocks the upload.
      // T22.170: Cancel pressed after the bytes landed (moderation / the
      // web-safe check below still running) must still cancel — never hand
      // the cancelled upload to the block.
      if (controller?.signal.aborted) throw new UploadCancelledError();
      let finalLocation = location;
      if (isVideo) {
        try {
          let result = (await checkSegmentVideoWeb(location))?.data?.data;
          const deadline = Date.now() + SEGMENT_VIDEO_WEB_MAX_WAIT_MS;
          if (result?.pending) setConverting(true);
          let pollErrors = 0;
          while (result?.pending && Date.now() < deadline) {
            await waitOrAbort(SEGMENT_VIDEO_WEB_POLL_MS, controller.signal);
            if (controller.signal.aborted) break;
            try {
              result = (await checkSegmentVideoWeb(location))?.data?.data;
              pollErrors = 0;
            } catch (err) {
              pollErrors += 1;
              if (
                !isRetryablePollError(err) ||
                pollErrors >= SEGMENT_VIDEO_WEB_MAX_POLL_ERRORS
              ) {
                throw err;
              }
            }
          }
          if (result?.url && !result.pending) {
            finalLocation = result.url;
          }
        } catch (err) {
          console.log(err);
        }
        if (controller.signal.aborted) throw new UploadCancelledError();
      }

      handleMedia({
        id,
        src: finalLocation,
        title: file.name,
        loading: false,
      });
      endPending();

      try {
        // review-1008-Frontend-6615: Recent Media must offer the same
        // web-safe url the block itself just got (finalLocation), not the
        // raw upload -- otherwise re-picking this clip from the Recent tab
        // hands a later block the un-transcoded HEVC source and reproduces
        // the exact "Video unavailable" failure checkSegmentVideoWeb exists
        // to prevent.
        await saveRecentMedia(wheelId, {
          type: getFileType(),
          url: finalLocation,
          metadata: {
            type: file.type,
            title: file.name,
          },
        });
      } catch (err) {
        console.log(err);
      }

      setLoading(false);
    } catch (err) {
      handleMedia({ id, loading: false });
      // A user-initiated Cancel is not a failure — no error toast, just a
      // quiet return to the idle dropzone (matches Upload.jsx's B2-MEDIA-001
      // precedent).
      if (!(err instanceof UploadCancelledError || err?.cancelled === true)) {
        toast.error(
          toErrorToastMessage(
            err,
            type === "video"
              ? "The video could not be uploaded. Try again or use a smaller file."
              : "The file could not be uploaded. Please try again."
          )
        );
      }
      setLoading(false);
    } finally {
      if (isVideo) {
        abortControllerRef.current = null;
        setPercent(null);
        setConverting(false);
      }
      if (tracked) endActiveUpload(uploadKey, cancel);
      endPending();
    }
  };

  const uploadFiles = async (inputFiles) => {
    setError(false);
    // stgfix-1008-B50: force-clear any rejection toast still on screen from a
    // PRIOR attempt the moment a new one starts — see rejectionToastIdsRef
    // above. Does nothing if nothing of ours is showing.
    dismissRejectionToasts();
    // CAP-UP-04 + C4.1/C4.2: validate type AND size uniformly BEFORE any upload
    // work. getValidFiles drops wrong-type files (C4.2 — the picker no longer
    // bypasses the type check) and oversize files (C4.1 — the single/video
    // branch is now size-guarded too), toasting per rejected axis. An empty
    // result (e.g. a drop where EVERY file failed) returns cleanly instead of
    // falling through to the single-file branch and reading `files[0]` on
    // `undefined`.
    const files = getValidFiles(inputFiles);
    if (files.length === 0) {
      return;
    }

    // kelly-1005-P1-portrait-video-length-before-upload measures the file's
    // OWN duration client-side (hidden <video> + loadedmetadata, no network
    // call) BEFORE any presign/S3 work starts. gordon-1005-P2-portrait-video-
    // trimmer changes what happens next: Gordon, 2026-10-05 20:55 PT — OFFER
    // A TRIMMER instead of only refusing. `null` (unreadable metadata) is
    // still NOT a rejection: the file proceeds to the normal upload/server
    // path, same contract validatePortraitVideoFile already uses for an
    // unknown duration.
    if (type === "video" && videoMaxDurationSeconds) {
      const duration = await readLocalVideoDurationSeconds(files[0]);
      if (duration != null && duration > videoMaxDurationSeconds) {
        setTrimCandidate({ file: files[0], duration });
        return;
      }
    }

    if (type === "images" || type == "documents" || type == "audio") {
      const endPending = beginPendingUpload();
      try {
        setLoading(true);
        const recentFiles = [];
        for (const file of files) {
          const id = getRandomId();
          try {
            handleMedia({ id, loading: true });
            const result = await handleFileUpload(
              file,
              uploadPath,
              wheelId,
              null
            );
            const fileExtension = file.name.split(".").pop();

            const uploadedFile = {
              id: id,
              src: result.location,
              title: file.name,
              loading: false,
              type: fileExtension,
            };
            recentFiles.push({
              type: getFileType(),
              url: result.location,
              metadata: {
                title: file.name,
                type: fileExtension,
              },
            });
            handleMedia(uploadedFile);
          } catch (err) {
            const filteredFiles = media.filter((file) => file.id !== id);
            setMedia(filteredFiles);
            setLoading(false);
            console.log(err);
            toast.error(
              toErrorToastMessage(err, "This file couldn't be uploaded. Please try again.")
            );
          }
        }

        setLoading(false);
        endPending();

        // Save each file using saveRecentMedia
        if (recentFiles.length > 0) {
          try {
            await Promise.all(
              recentFiles.map(async (file) => {
                await saveRecentMedia(wheelId, file);
              })
            );
          } catch (apiError) {
            console.log("Failed to save some files.");
          }
        }
      } catch (err) {
        toast.error(
          toErrorToastMessage(err, "Some files couldn't be uploaded. Please try again.")
        );
        setLoading(false);
      } finally {
        endPending();
      }
    } else {
      await uploadSingleFile(files[0]);
    }
  };

  // T22.143: a video upload for this block that is still running — possibly
  // started by an earlier, since-unmounted mount of this pane.
  const activeUpload = useActiveUpload(type === "video" ? uploadKey : null);
  const showLoading = loading || !!activeUpload;
  const shownPercent = activeUpload ? activeUpload.percent : percent;
  const loaderCaption = converting
    ? "Converting video for web playback…"
    : shownPercent != null
    ? `Uploading… ${shownPercent}%`
    : "Uploading…";

  const handleCancelUpload = () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    else if (activeUpload) activeUpload.cancel();
  };

  // CAP-UP-01 (page-wide drop hijack): these listeners used to be bound to
  // `window` with an upload handler attached, which meant (a) a file dropped
  // ANYWHERE on the page — the header, the wheel canvas, a text field — was
  // uploaded into this picker, even though the zone's own copy promises "Drag
  // and Drop <type> here", and (b) with N UploadMedia instances mounted at once
  // (Wallpaper image + Video block settings both mount it directly, outside
  // MediaSelection's one-panel-at-a-time guard) a SINGLE drop fired N handlers,
  // producing duplicate uploads and cross-type "not supported" errors.
  // They also ran from a `[]`-dep effect, so the handler kept the FIRST render's
  // `uploadFiles`/`media`/`wheelId` closure — a stale `setMedia()` on error.
  //
  // Window now only keeps the ONE behaviour that has to be global: stopping the
  // browser's default "navigate away and open the dropped file" on a miss. It no
  // longer uploads. The upload handlers moved onto the dropzone itself (below),
  // where they re-bind every render, so no stale closure remains.
  useEffect(() => {
    const preventBrowserFileOpen = (e) => {
      e.preventDefault();
    };

    window.addEventListener("drop", preventBrowserFileOpen);
    window.addEventListener("dragover", preventBrowserFileOpen);

    return () => {
      window.removeEventListener("drop", preventBrowserFileOpen);
      window.removeEventListener("dragover", preventBrowserFileOpen);
    };
  }, []);

  // CAP-UP-02: the drag-active affordance was DEAD — `const isActive = false`
  // was hardcoded, so the green border in `borderColor: isActive ? green : purple`
  // could never render and a dragging user got no "you can drop here" feedback.
  // Wire it to real drag events. `dragDepth` counts enter/leave pairs so moving
  // the pointer over a CHILD node (the icon, the label) does not flicker the
  // state off — dragleave fires for every descendant boundary.
  const [isActive, setIsActive] = useState(false);
  const dragDepth = useRef(0);

  const resetDragState = () => {
    dragDepth.current = 0;
    setIsActive(false);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current += 1;
    setIsActive(true);
  };

  const handleDragOver = (e) => {
    // Required: without preventDefault on dragover the element is not a valid
    // drop target and `drop` never fires.
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "copy";
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) {
      setIsActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    resetDragState();
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) {
      return;
    }
    // Out of storage: refuse the DROP too, not just the click.
    if (blockUpload()) return;
    // uploadFiles runs the shared type + size guard (getValidFiles).
    uploadFiles(files);
  };

  // CAP-A11Y-02 (WCAG 2.1.1 / 4.1.2): the dropzone was a bare `<div onClick>`
  // (role=null, tabIndex=-1, no accessible name), so keyboard and screen-reader
  // users could not open the file picker AT ALL. It stays a div — `TextView`
  // renders a `<p>`, and a `<p>` inside a `<button>` is invalid DOM nesting —
  // so it takes the standard ARIA button pattern instead: role + tabIndex +
  // Enter/Space activation + an accessible name.
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      handleClick();
    }
  };

  const getPlaceholderText = () => {
    switch (type) {
      case "image":
      case "images":
        return "Drag and Drop image here or";
      case "video":
        return "Drag and Drop video here or";
      case "documents":
        return "Drag and Drop document here or";
      case "document":
        return "Drag and Drop document here or";
      case "audio":
        return "Drag and Drop audio file here or";
      default:
        return "Drag and Drop file here or";
    }
  };

  const getAcceptedFileTypes = () => {
    switch (type) {
      case "image":
      case "images":
        return IMAGE_ACCEPT;
      case "video":
        return "video/mp4, video/mov, video/avi, video/mkv, video/quicktime";
      case "documents":
        return "application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation, text/csv";
      case "document":
        return "application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation, text/csv";
      // QA-97 — extensions, not a MIME list. The MIME list silently excluded
      // every container the browser has no registered type for (.m4b, .aa,
      // .aax), so the picker greyed out the exact files the ticket is about.
      case "audio":
        return audioAcceptAttribute();
      case "audios":
        return audioAcceptAttribute();
      default:
        return "";
    }
  };

  if (isCheckingEligibility) {
    return (
      <Container>
        <BrandWheelLoader variant="medium" label="Checking eligibility…" />
      </Container>
    );
  }

  if (!isEligible) {
    return (
      <FeatureEligibility
        subscription={subscription}
        requiredSubscription={requiredSubscription}
      />
    );
  }

  return (
    <>
      <Container
        style={{
          borderColor: isActive ? colors.green : colors.purple,
        }}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        role="button"
        tabIndex={0}
        /* ISP-9999: out of storage. `aria-disabled` (not the `disabled`
           attribute, which a div cannot carry anyway) announces the state and
           the REASON, and the dimmed style shows it. The handlers above still
           run, so an attempt explains itself instead of failing silently. */
        aria-disabled={storageFull ? "true" : undefined}
        data-storage-full={storageFull ? "true" : undefined}
        title={storageFull ? storageFullLabel : undefined}
        aria-label={
          storageFull
            ? `Choose ${type} to upload — ${storageFullLabel}`
            : `Choose ${type} to upload, or drag and drop a file here`
        }
        data-testid="upload-dropzone"
        data-drag-active={isActive ? "true" : "false"}
      >
        <HiddenInput
          type="file"
          ref={fileInputRef}
          onChange={handleMediaChange}
          accept={
            !allowGif && isImageType
              ? getAcceptedFileTypes().replace(/image\/gif,\s*/i, "")
              : getAcceptedFileTypes()
          }
          multiple={
            type === "images" || type === "documents" || type == "audio"
          }
        />

        {showLoading ? (
          type === "video" ? (
            <>
              {/* stgfix-1008-B48 (Kelly, 2026-10-07): `label` only ever reaches
                  BrandWheelLoader's `bwl-visually-hidden` span (the a11y name) —
                  a sighted user uploading a video still saw a bare spinner for
                  the whole PUT, with the gordon-0923 `percent` state tracked
                  but never drawn anywhere. `caption` is BrandWheelLoader's only
                  on-screen text mechanism; reusing the same string here makes
                  the already-tracked progress actually visible.
                  stgfix-1008-B49: video uploads can run many seconds and already
                  show real determinate progress via `percent` — the shared
                  loader's indeterminate generation loop would otherwise cycle
                  back to an empty ring every ~2s (#908 START EMPTY), reading
                  as the upload stalling/restarting. loop={false} keeps the
                  complete Wheel on screen for the whole upload. */}
              <BrandWheelLoader
                variant="medium"
                label={loaderCaption}
                caption={loaderCaption}
                loop={false}
              />
              {/* T22.141 (Kelly 2026-10-12): the default filled Button is
                  width:100% -> a full-width purple bar across the dropzone.
                  Cancel is a secondary action: the app's outlined button,
                  sized to its label. */}
              <Button
                variant="outlined"
                width="auto"
                style={{ width: "auto", marginTop: 8 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelUpload();
                }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <BrandWheelLoader variant="medium" label="Uploading…" />
          )
        ) : (
          <>
            <Icon src={fileIcons[type]} style={storageFull ? { opacity: 0.5 } : undefined} />
            {/* kelly-1005-P1-portrait-video-length-before-upload: the length
                cap in PLAIN WORDS, where people decide — before they ever pick
                a file. File size stays in the small-print "Valid formats" line
                below, never the headline. */}
            {type === "video" && videoMaxDurationSeconds && (
              <TextView
                style={{ textAlign: "center", fontWeight: 700 }}
                data-testid="upload-video-duration-cap"
              >
                {videoDurationCapLabel(videoMaxDurationSeconds, videoDurationContextLabel)}
              </TextView>
            )}
            <TextView style={{ textAlign: "center" }}>
              {getPlaceholderText()}{" "}
              <BoldText>
                Choose {type.charAt(0).toUpperCase() + type.slice(1)}
              </BoldText>
            </TextView>
            {/* The reason ON SCREEN, not only in a tooltip a touch user can
                never open and a keyboard user only hears on focus. */}
            {storageFull && (
              <TextView
                style={{ textAlign: "center", opacity: 0.8 }}
                data-testid="upload-storage-full-note"
              >
                {storageFullLabel}
              </TextView>
            )}
          </>
        )}
      </Container>
      <InfoContainer
        style={{
          backgroundColor: colors.lightBorder,
        }}
      >
        <TextView>
          <span style={{ fontWeight: "bold" }}>{"Valid formats: "}</span>{" "}
          {effectiveFormats.join(", ")}
          {/* File size: fine print only, never the headline (kelly-1005-P1). */}
          {/* T22.676: every video surface states its cap up front — the
              Segment Video block (no videoMaxBytes) is held to the generic
              video cap (T22.677: 256MB), and used to say nothing until a pick failed.
              A duration-capped surface without its own videoMaxBytes keeps
              size out of view (kelly-1005-P1). */}
          {type === "video" && (videoMaxBytes || !videoMaxDurationSeconds)
            ? ` · up to ${
                videoMaxBytes
                  ? `${Math.round(videoMaxBytes / (1024 * 1024))}MB`
                  : VIDEO_MAX_FILE_SIZE_LABEL
              }`
            : ""}
        </TextView>
      </InfoContainer>

      {error && (
        <InfoContainer
          style={{
            backgroundColor: "#FFEFEF",
          }}
        >
          <TextView>
            <b>Error:</b>{" "}
            {typeof error === "string" ? error : `Upload exceeds ${maxFileSizeLabelForType(type)} limit.`}
          </TextView>
        </InfoContainer>
      )}

      {/* gordon-1005-P2-portrait-video-trimmer: a video over
          videoMaxDurationSeconds lands here instead of a toast rejection.
          Suspense's fallback only shows for the one-time chunk fetch; every
          later too-long pick in the same session mounts instantly. */}
      {trimCandidate && (
        <Suspense fallback={<BrandWheelLoader variant="medium" label="Loading trimmer…" />}>
          <VideoTrimmer
            file={trimCandidate.file}
            durationSeconds={trimCandidate.duration}
            maxDurationSeconds={videoMaxDurationSeconds}
            contextLabel={videoDurationContextLabel}
            onCancel={() => setTrimCandidate(null)}
            onConfirm={async (trimmedFile) => {
              setTrimCandidate(null);
              await uploadSingleFile(trimmedFile);
            }}
          />
        </Suspense>
      )}
    </>
  );
}
